import React, { useState } from "react";
import api from "../api/homeapi";
import PublicNav from "./PublicNav";
import { InlineAlert } from "./Notify";

const C = {
  green: "#006B3C", greenDk: "#004D2B", greenLt: "#E8F5EE",
  yellow: "#FFC300", yellowLt: "#FFF8DC",
  white: "#FFFFFF", gray: "#F5F5F5", grayBd: "#E0E0E0",
  text: "#1A1A1A", textSub: "#666666",
};

/**
 * Page vitrine réutilisable pour les services annexes de SETRAG (transport
 * de véhicules, transport de marchandises) : présentation du service +
 * formulaire de demande simple, envoyé sur la messagerie interne (visible
 * du support et de l'administrateur) via /contact avec un sujet dédié -
 * volontairement minimaliste (pas de flux de réservation complet), pour
 * donner à l'équipe SETRAG de quoi répondre concrètement lors d'une
 * présentation sans avoir eu à développer toute la logique métier.
 */
export default function ServicePage({ title, subtitle, subject, formIntro, routeLabel, features, children }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", route: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSend = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Merci de renseigner votre nom et un numéro pour vous recontacter.");
      return;
    }
    setError("");
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject,
        message: `${routeLabel ? `Trajet : ${form.route}\n` : ""}${form.message}`,
      });
      setSent(true);
    } catch {
      setError("Une erreur est survenue lors de l'envoi. Merci de réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Segoe UI',sans-serif", color: C.text, minHeight: "100vh" }}>
      {/* NAVBAR (identique à la page d'accueil) */}
      <PublicNav />

      {/* HERO */}
      <div style={{ background: `linear-gradient(135deg,${C.greenDk} 0%,${C.green} 60%,#1E7A4A 100%)`, padding: "56px 24px 64px", textAlign: "center" }}>
        <h1 style={{ color: "#fff", fontSize: "clamp(26px,4.5vw,40px)", fontWeight: 800, margin: "0 0 12px" }}>{title}</h1>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 15, maxWidth: 560, margin: "0 auto" }}>{subtitle}</p>
      </div>

      {/* FEATURES */}
      <div style={{ maxWidth: 900, margin: "-32px auto 0", padding: "0 16px" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 12px 40px rgba(0,0,0,0.1)", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16 }}>
          {features.map(([Ic, ftitle, desc]) => (
            <div key={ftitle} style={{ background: C.greenLt, borderRadius: 12, padding: 18, textAlign: "center" }}>
              <div style={{ color: C.green, display: "flex", justifyContent: "center", marginBottom: 8 }}><Ic size={28} /></div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{ftitle}</div>
              <div style={{ fontSize: 12, color: C.textSub, lineHeight: 1.5 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {children}

      {/* FORMULAIRE DE DEMANDE */}
      <div style={{ maxWidth: 560, margin: "40px auto", padding: "0 16px 60px" }}>
        <div style={{ background: "#fff", border: `1px solid ${C.grayBd}`, borderRadius: 16, padding: 28 }}>
          {!sent ? (
            <>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 6px", color: C.greenDk }}>Faire une demande</h2>
              <p style={{ fontSize: 13, color: C.textSub, margin: "0 0 20px", lineHeight: 1.6 }}>{formIntro}</p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <Field label="Nom complet *" value={form.name} onChange={update("name")} placeholder="Jean-Pierre Moussavou" />
                <Field label="Téléphone *" value={form.phone} onChange={update("phone")} placeholder="074 00 00 00" type="tel" />
              </div>
              <Field label="Email (optionnel)" value={form.email} onChange={update("email")} placeholder="vous@exemple.ga" type="email" style={{ marginBottom: 12 }} />
              {routeLabel && <Field label={routeLabel} value={form.route} onChange={update("route")} placeholder="Ville de départ → Ville d'arrivée" style={{ marginBottom: 12 }} />}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Détails de votre demande</label>
                <textarea value={form.message} onChange={update("message")} placeholder="Décrivez votre besoin (quantité, poids, dimensions, date souhaitée...)"
                  style={{ ...inputStyle, height: 90, resize: "vertical" }} />
              </div>

              {error && <InlineAlert type="error">{error}</InlineAlert>}

              <button onClick={handleSend} disabled={sending}
                style={{ width: "100%", background: C.green, color: "#fff", border: "none", borderRadius: 12, padding: 14, fontFamily: "'Segoe UI',sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: sending ? 0.7 : 1 }}>
                {sending ? "Envoi en cours…" : "Envoyer ma demande →"}
              </button>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.greenLt, border: `3px solid ${C.green}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 26, color: C.green }}>✓</div>
              <h3 style={{ color: C.green, fontSize: 17, margin: "0 0 6px" }}>Demande envoyée !</h3>
              <p style={{ color: C.textSub, fontSize: 13 }}>Un agent SETRAG vous recontactera prochainement au {form.phone}.</p>
            </div>
          )}
        </div>
      </div>

      {/* FOOTER simplifié */}
      <footer style={{ background: C.greenDk, color: "rgba(255,255,255,0.7)", padding: "32px 24px", textAlign: "center" }}>
        <div style={{ fontWeight: 800, color: "#fff", fontSize: 15, marginBottom: 6 }}>SETRAG</div>
        <p style={{ fontSize: 12, margin: 0 }}>© {new Date().getFullYear()} SETRAG - Tous droits réservés</p>
      </footer>
    </div>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 };
const inputStyle = { width: "100%", border: `1.5px solid ${C.grayBd}`, borderRadius: 10, padding: "11px 14px", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };

const Field = ({ label, style, ...props }) => (
  <div style={style}>
    <label style={labelStyle}>{label}</label>
    <input style={inputStyle} {...props} />
  </div>
);
